/**
 * postNavContext.ts — Thin module-level store that carries the profile grid
 * list + start index from UserPosts.openPost to the modal renderer.
 *
 * UserPosts writes here just before dispatching the popstate event; appModals
 * reads it when opening the viewingComments modal (without clearing it, so it
 * is stable across re-renders of the open modal), and clears it when the modal
 * closes.
 *
 * Lifecycle:
 *   1. UserPosts.openPost → setPendingPostNavContext(ctx)
 *   2. appModals: viewingComments first open → readPostNavContext() (stable prop)
 *   3. appModals: modal closes → clearPostNavContext()
 *
 * This avoids encoding the full list in the URL or Firestore, keeps the
 * cold-load deep-link path (no list → arrows simply don't render), and has
 * zero impact on the mobile onOpen path.
 */

export interface PostNavContext {
  postList: { id: string; type?: string; [key: string]: any }[];
  startIndex: number;
}

let _ctx: PostNavContext | null = null;

/** Called by UserPosts.openPost immediately before dispatching the popstate event. */
export function setPendingPostNavContext(ctx: PostNavContext): void {
  _ctx = ctx;
}

/**
 * Called by appModals on each render while viewingComments is open.
 * Returns the stored context (stable across re-renders) or null for cold deep-link opens.
 */
export function readPostNavContext(): PostNavContext | null {
  return _ctx;
}

/**
 * Called by appModals when the viewingComments modal closes.
 * Clears the stored context so it does not bleed into the next modal open.
 */
export function clearPostNavContext(): void {
  _ctx = null;
}
